import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Loader2, UserCheck, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { fmtDateTime } from "@/lib/format";

const EMPTY = { name: "", designation: "", email: "", contact_number: "", department: "" };

export default function TechnicalReviewers() {
  const [reviewers, setReviewers] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [created, setCreated] = useState(null);

  const load = () => api.get("/technical-reviewers").then((r) => setReviewers(r.data.reviewers)).catch(() => toast.error("Failed to load technical reviewers"));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.post("/technical-reviewers", form);
      setCreated(r.data);
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  return (
    <div data-testid="technical-reviewers-page" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Technical Reviewers</h1>
          <p className="text-sm text-muted-foreground">Manage technical reviewers from your organization.</p>
        </div>
        <Button data-testid="add-reviewer-btn" onClick={() => setOpen(true)} className="bg-slate-900 hover:bg-slate-800">
          <Plus className="mr-1.5 h-4 w-4" /> Add Technical Reviewer
        </Button>
      </div>

      <div className="overflow-x-auto rounded-lg border bg-card">
        <table className="w-full text-sm" data-testid="reviewers-table">
          <thead>
            <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Designation</th>
              <th className="px-4 py-3 font-medium">Department</th>
              <th className="px-4 py-3 font-medium">Email</th>
              <th className="px-4 py-3 font-medium">Contact</th>
              <th className="px-4 py-3 font-medium text-right">Pending</th>
              <th className="px-4 py-3 font-medium text-right">Completed</th>
              <th className="px-4 py-3 font-medium">Added</th>
            </tr>
          </thead>
          <tbody>
            {reviewers.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-muted-foreground" data-testid="reviewers-empty">
                No technical reviewers yet. Add one to start sending quotations for technical evaluation.
              </td></tr>
            )}
            {reviewers.map((r) => (
              <tr key={r.id} data-testid={`reviewer-row-${r.email}`} className="border-b last:border-0">
                <td className="px-4 py-3"><p className="flex items-center gap-2 font-medium"><UserCheck className="h-4 w-4 text-slate-400" />{r.name}</p></td>
                <td className="px-4 py-3 text-sm">{r.designation}</td>
                <td className="px-4 py-3 text-sm">{r.department}</td>
                <td className="px-4 py-3 text-xs text-muted-foreground">{r.email}</td>
                <td className="px-4 py-3 font-mono text-xs">{r.contact_number}</td>
                <td className="px-4 py-3 text-right font-mono">{r.pending_reviews}</td>
                <td className="px-4 py-3 text-right font-mono">{r.completed_reviews}</td>
                <td className="px-4 py-3 text-xs text-muted-foreground">{fmtDateTime(r.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white" data-testid="add-reviewer-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Add Technical Reviewer</DialogTitle>
            <DialogDescription>The reviewer receives an email invitation (mock) and can evaluate technical quotations only — never pricing.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Name *</Label><Input data-testid="reviewer-form-name" required className="mt-1.5" placeholder="Ankit Sharma" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>Designation *</Label><Input data-testid="reviewer-form-designation" required className="mt-1.5" placeholder="Senior Technical Manager" value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} /></div>
              <div><Label>Email *</Label><Input data-testid="reviewer-form-email" required type="email" className="mt-1.5" placeholder="ankit.sharma@acme.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><Label>Contact Number *</Label><Input data-testid="reviewer-form-contact" required className="mt-1.5" placeholder="+91 98XXX XXXXX" value={form.contact_number} onChange={(e) => setForm({ ...form, contact_number: e.target.value })} /></div>
              <div className="col-span-2"><Label>Department *</Label><Input data-testid="reviewer-form-department" required className="mt-1.5" placeholder="Engineering" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="reviewer-form-submit" disabled={saving} className="bg-slate-900 hover:bg-slate-800">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Add Technical Reviewer
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!created} onOpenChange={() => setCreated(null)}>
        <DialogContent className="bg-white" data-testid="reviewer-created-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Reviewer Added</DialogTitle>
            <DialogDescription>Invitation email logged (mock service — no real email sent). Share these credentials with the reviewer:</DialogDescription>
          </DialogHeader>
          <div className="rounded-md border bg-slate-50 p-4 font-mono text-sm" data-testid="reviewer-credentials">
            <p>Email: {created?.email}</p>
            <p className="mt-1">Temporary password: <span className="font-semibold">{created?.temp_password}</span></p>
          </div>
          <DialogFooter>
            <Button variant="outline" data-testid="copy-credentials-btn" onClick={() => { navigator.clipboard.writeText(`${created?.email} / ${created?.temp_password}`); toast.success("Copied"); }}>
              <Copy className="mr-1.5 h-4 w-4" /> Copy
            </Button>
            <Button data-testid="reviewer-created-close" onClick={() => setCreated(null)} className="bg-slate-900 hover:bg-slate-800">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
